import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type LeadStatus = 'new' | 'in_progress' | 'qualified' | 'closed' | 'spam';

export type AdminLead = {
  id: string;
  name: string;
  email: string;
  company?: string;
  topic?: string;
  message: string;
  status: LeadStatus;
  createdAt: string;
  lastUpdated: string;
  notes?: string;
  assignedTo?: string;
};

export type LeadFilters = {
  status?: LeadStatus;
  query?: string;
};

const mockLeads: AdminLead[] = [
  {
    id: 'lead-101',
    name: 'Sarah Khan',
    email: 'sarah.khan@oakandpine.co.uk',
    company: 'Oak & Pine Care',
    topic: 'enterprise',
    message: 'We are opening two new flexible care hubs and need 25 hires over the next 60 days.',
    status: 'new',
    createdAt: '2025-11-13T09:05:00.000Z',
    lastUpdated: '2025-11-13T09:05:00.000Z',
    assignedTo: 'Mara',
  },
  {
    id: 'lead-102',
    name: 'James Patel',
    email: 'j.patel@northstarhealth.com',
    company: 'Northstar Health',
    topic: 'pricing',
    message: 'Looking for pricing for 5 concurrent roles plus talent search add-on.',
    status: 'in_progress',
    createdAt: '2025-11-12T14:20:00.000Z',
    lastUpdated: '2025-11-12T15:45:00.000Z',
    notes: 'Intro call booked for Friday.',
    assignedTo: 'Ella',
  },
  {
    id: 'lead-103',
    name: 'Harriet Wong',
    email: 'harriet@carelaunch.uk',
    company: 'Care Launch',
    topic: 'partnerships',
    message: 'We run a returner accelerator and want to partner on exclusive roles.',
    status: 'qualified',
    createdAt: '2025-11-11T11:00:00.000Z',
    lastUpdated: '2025-11-11T16:25:00.000Z',
    notes: 'Sent deck + partner overview.',
    assignedTo: 'Noah',
  },
  {
    id: 'lead-104',
    name: 'Spam Bot',
    email: 'bot@example.com',
    message: 'Cheap SEO services!!!',
    status: 'spam',
    createdAt: '2025-11-10T08:00:00.000Z',
    lastUpdated: '2025-11-10T08:05:00.000Z',
  },
];

const filterMockLeads = (filters: LeadFilters = {}) => {
  return mockLeads.filter((lead) => {
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.query) {
      const needle = filters.query.toLowerCase();
      const haystack = [lead.name, lead.email, lead.company, lead.message].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
};

export async function fetchAdminLeads(filters: LeadFilters = {}): Promise<AdminLead[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load admin leads.');
  }
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.query) params.set('q', filters.query);
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/leads?${params.toString()}`);
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Admin leads fetch failed (${response.status}): ${text}`);
  }
  const payload = await response.json();
  return (payload?.leads as AdminLead[]) ?? [];
}

export async function updateAdminLead(id: string, updates: Partial<Pick<AdminLead, 'status' | 'notes' | 'assignedTo'>>) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update admin leads.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Lead update failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminLead;
}

export async function markLeadAsSpam(id: string) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to mark lead as spam.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/leads/${id}/ban`, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Lead spam action failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminLead;
}

export async function deleteAdminLead(id: string) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete admin leads.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/leads/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Lead delete failed (${response.status}): ${text}`);
  }
  return { success: true };
}
